import React, { useCallback } from 'react';
import difference from 'lodash/difference';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import shuffle from 'lodash/shuffle';

import { useSelector } from 'react-redux';
import arrayMutators from 'final-form-arrays';
import { Form } from 'react-final-form';

import CharmaLogo from 'Shared/components/Logos/CharmaLogo';
import { AvatarType } from 'Shared/constants/types';

import CreateAccountModalContentArea from '../../../CreateAccountModal/CreateAccountModalContentArea/CreateAccountModalContentArea';

import OnboardingWizardStepsSection from '../OnboardingWizardWrappers/OnboardingWizardPageWrapper/OnboardingWizardStepsSection/OnboardingWizardStepsSection';
import OnboardingWizardButtonsArea from '../OnboardingWizardWrappers/OnboardingWizardContentWrapper/OnboardingWizardButtonsArea/OnboardingWizardButtonsArea';
import { roleOptions, usingWPOptions, usingWPOptionsForInvited, AVATAR_REQUIRED_MESSAGE } from '../../constants';
import { optionsDecorator, outputOptionsDecorator } from '../../utils';
import ProfileForm from './ProfileForm/ProfileForm';
import OnboardingTestimonial from './OnboardingTestimonial/OnboardingTestimonial';

import './Profile.scss';

type Props = {
  profile: {
    usingWP: Array<string>;
    role: string;
    fullName: string;
    avatar: AvatarType;
  };
  advanceOnboardingState: Function;
};

function usingWPDecorator(initialUsingWP, presetOptions) {
  const diff = difference(initialUsingWP, presetOptions);
  return optionsDecorator({ options: initialUsingWP, presetOptions, differenceOptions: diff.length ? diff : [] });
}
const ProfileWhiteList = Object.freeze(['fullName', 'usingWP', 'avatar', 'avatarType']);

function validate(values) {
  const errors = { profile: { avatarType: undefined, avatar: undefined } };
  if (values.profile.avatarType === 'picture' && !(values.profilePicture || values.profile.avatar.url))
    errors.profile.avatar = AVATAR_REQUIRED_MESSAGE;
  return errors;
}

function Profile({ profile, advanceOnboardingState }: Props) {
  const handleNextStepClick = useCallback(
    values => {
      const newProfile = values.profile;
      return advanceOnboardingState({
        profile: {
          ...pick(newProfile, ProfileWhiteList),
          role: newProfile.role?.value,
          usingWP: outputOptionsDecorator(newProfile.usingWP),
          avatar: omit(newProfile.avatar, 'url'),
        },
        profile_picture: values.profilePicture,
      });
    },
    [advanceOnboardingState]
  );

  const isInvited = useSelector(state => state.onboardingState?.flowType === 'invited');
  // const isCalendarIntegration = useSelector(state => state.onboardingState?.flowType === 'calendar_integration');

  const initialValues = {
    profile: {
      ...profile,
      avatarType: 'initials',
      usingWP: usingWPDecorator(profile.usingWP, shuffle(isInvited ? usingWPOptionsForInvited : usingWPOptions)),
      role:
        roleOptions.find(({ value }) => value === profile.role) || roleOptions.find(({ value }) => value === 'manager'),
    },
  };

  const handleKeyPress = useCallback(event => {
    if (event.which === 13 /* Enter */) event.preventDefault();
  }, []);

  return (
    <Form
      mutators={{ ...arrayMutators }}
      initialValues={initialValues}
      onSubmit={handleNextStepClick}
      validate={validate}
    >
      {({ handleSubmit }) => (
        <form className="profile d-flex" onKeyPress={handleKeyPress} onSubmit={handleSubmit} autoComplete="off">
          {false && <CreateAccountModalContentArea showCompletedSteps={false} />}
          {true && (
            <div className="profile-testimonial-container">
              <OnboardingTestimonial />
            </div>
          )}

          <div className="profile-form-container d-flex flex-column justify-content-center align-items-center">
            <div className="profile-form-container-logo w-100 mb-lg-0">
              <CharmaLogo width="230" />
            </div>

            <div className="profile-form-content d-flex flex-column w-100 mb-auto">
              <div className="mb-3">
                <OnboardingWizardStepsSection />
              </div>
              <ProfileForm />
            </div>
            <OnboardingWizardButtonsArea />
          </div>
        </form>
      )}
    </Form>
  );
}

export default Profile;
